import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronDown, Menu, X } from "lucide-react";
import { useLang } from "@/i18n/LangContext";
import Logo from "./Logo";

export default function Header() {
  const { t, link, lang } = useLang();
  const { pathname } = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  const cleanPath = pathname.replace(/^\/en/, "") || "/";
  const grPath = cleanPath;
  const enPath = `/en${cleanPath === "/" ? "" : cleanPath}`;

  const navItems = [
    { label: t("nav.home"), to: link("/") },
    { label: t("nav.projects"), to: link("/projects") },
    { label: t("nav.solutions"), to: link("/solutions") },
    { label: t("nav.whyB2b"), to: `${link("/")}#why-b2b` },
    { label: t("nav.about"), to: link("/about") },
    { label: t("nav.contact"), to: link("/contact") },
  ];

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-300 ${
        scrolled || open ? "glass-dark border-b border-white/10" : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-[72px]">
          <Logo />

          <nav className="hidden lg:flex items-center gap-7" aria-label="Main">
            <Link to={link("/")} className="text-sm text-steel hover:text-white transition-colors">{t("nav.home")}</Link>
            <div className="relative group">
              <button data-testid="nav-services-dropdown" className="flex items-center gap-1 text-sm text-steel hover:text-white transition-colors py-2">
                {t("nav.services")}
                <ChevronDown className="h-3.5 w-3.5 transition-transform group-hover:rotate-180" />
              </button>
              <div className="absolute left-1/2 -translate-x-1/2 top-full pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-[opacity,visibility] duration-200">
                <div className="glass-dark border border-white/10 rounded-xl p-2 w-56 shadow-2xl">
                  {t("nav.dropdown").map((d) => (
                    <Link key={d.to} to={link(d.to)} data-testid={`nav-service-${d.to.slice(1)}`} className="block px-3 py-2.5 rounded-lg text-sm text-steel hover:text-white hover:bg-white/5 transition-colors">
                      {d.label}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
            {navItems.slice(1).map((item) => (
              <Link key={item.label} to={item.to} className="text-sm text-steel hover:text-white transition-colors">
                {item.label}
              </Link>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-5">
            <div className="flex items-center gap-1 text-xs font-semibold tracking-widest" data-testid="lang-switcher">
              <Link to={grPath} data-testid="lang-gr" className={lang === "gr" ? "text-electric" : "text-steel hover:text-white transition-colors"}>GR</Link>
              <span className="text-white/20">|</span>
              <Link to={enPath} data-testid="lang-en" className={lang === "en" ? "text-electric" : "text-steel hover:text-white transition-colors"}>EN</Link>
            </div>
            <Link
              to={link("/contact")}
              data-testid="header-cta"
              className="rounded-full bg-gradient-to-r from-dblue to-electric px-5 py-2.5 text-sm font-semibold text-white transition-transform duration-200 hover:scale-[1.04] hover:shadow-[0_0_24px_rgba(33,167,255,0.4)]"
            >
              {t("nav.cta")}
            </Link>
          </div>

          <button data-testid="mobile-menu-toggle" className="lg:hidden p-2 text-white" onClick={() => setOpen(!open)} aria-label="Menu" aria-expanded={open}>
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden glass-dark border-t border-white/10" data-testid="mobile-menu">
          <nav className="px-6 py-6 flex flex-col gap-1" aria-label="Mobile">
            <Link to={link("/")} className="py-3 text-base text-white/90">{t("nav.home")}</Link>
            <p className="pt-3 pb-1 text-[10px] tracking-[0.25em] text-steel">{t("nav.services").toUpperCase()}</p>
            {t("nav.dropdown").map((d) => (
              <Link key={d.to} to={link(d.to)} className="py-2.5 pl-3 text-sm text-steel">{d.label}</Link>
            ))}
            {navItems.slice(1).map((item) => (
              <Link key={item.label} to={item.to} className="py-3 text-base text-white/90">{item.label}</Link>
            ))}
            <div className="flex items-center gap-3 pt-4 text-xs font-semibold tracking-widest">
              <Link to={grPath} className={lang === "gr" ? "text-electric" : "text-steel"}>GR</Link>
              <span className="text-white/20">|</span>
              <Link to={enPath} className={lang === "en" ? "text-electric" : "text-steel"}>EN</Link>
            </div>
            <Link to={link("/contact")} data-testid="mobile-cta" className="mt-4 rounded-full bg-gradient-to-r from-dblue to-electric px-5 py-3.5 text-center text-sm font-semibold">
              {t("nav.cta")}
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
